import * as React from "react";
import { Spinner } from "@fluentui/react-components";
import type { MailLinkDto } from "../../services/mailLinkService";
import { linkedMailService, type LinkedMailContentDto } from "../../services/linkedMailService";
import { graphMailService, mapItem, type Email } from "../../services/graphMailService";
import { MailFileViewer, type MailQuelle } from "../documents/MailFileViewer";
import { MailCaptureStatus } from "./MailCaptureStatus";
import { MailDetailWindowShell } from "./MailDetailWindowShell";
import { openMailDetailWindow } from "./MailDetailWindow";
import type { OpenWindowConfig } from "../windowManager/WindowManagerContext";

/**
 * Eine verknüpfte Mail (Vorgang, Verlauf) — angezeigt wie jede andere Mail.
 *
 * User 26.10.2026: „keine Buttons ‚Outlook öffnen‘, ‚Original herunterladen‘,
 * sondern die Mail normal anzeigen … das Sichern immer im Hintergrund." Bis
 * dahin stand die gesicherte Kopie vorne, mit zwei Knöpfen darüber, und wer
 * antworten wollte, musste erst „Outlook-Aktionen öffnen".
 *
 * Jetzt kommt die Mail aus dem Postfach (`fetchMessageBody`, findet auch
 * verschobene Mails wieder) — mit Antworten, Drucken und Herunterladen wie
 * überall. Die gesicherte Kopie springt nur ein, wenn die Mail dort nicht mehr
 * zu holen ist. Das Sichern selbst läuft still: Ist ein erneuter Versuch
 * erlaubt, stößt das Fenster ihn einmal an, ohne etwas anzuzeigen.
 */
export function LinkedMailWindow({ link, openWindow }: { link: MailLinkDto; openWindow: (config: OpenWindowConfig) => void }) {
    const [content, setContent] = React.useState<LinkedMailContentDto | null>(null);
    const [error, setError] = React.useState("");
    /** Die Mail aus dem Postfach; `null` = wird noch geholt, `"fehlt"` = dort nicht mehr zu holen. */
    const [live, setLive] = React.useState<Email | "fehlt" | null>(null);
    const sicherungAngestossen = React.useRef(false);

    React.useEffect(() => {
        let alive = true;
        setContent(null); setError(""); setLive(null);
        linkedMailService.read(link.id)
            .then((result) => {
                if (!alive) return;
                setContent(result);
                if (result.source === "live") setLive(mapItem(result.mail, result.mail));
                // Sichern im Hintergrund — einmal je Fenster, Ergebnis egal.
                if (result.archive.retryAllowed && !sicherungAngestossen.current) {
                    sicherungAngestossen.current = true;
                    void linkedMailService.retry(link.id).catch(() => undefined);
                }
            })
            .catch(() => {
                // T-220: Grund und Ausweg nennen statt nur „nicht abrufbar“.
                if (alive) setError("Die verknüpfte Mail ist nicht abrufbar. Entweder sind Sie an diesem Vorgang nicht beteiligt (Hauptverantwortlich, Bearbeiter, zu informieren, Frontoffice oder Backoffice des Kunden), oder die Mail wurde im Postfach gelöscht. Bitte wenden Sie sich an einen Beteiligten des Vorgangs.");
            });
        return () => { alive = false; };
    }, [link.id]);

    // Liegt eine gesicherte Kopie vor, trotzdem zuerst das Postfach fragen.
    // T-217: Den Ausschlag gibt die Leseentscheidung des Servers (Postfach-Grant
    // ODER Vorgang); geantwortet wird aus dem eigenen Postfach.
    React.useEffect(() => {
        if (content?.source !== "archive") return;
        let alive = true;
        graphMailService.fetchMessageBody(link.mailbox, link.messageId)
            .then((email) => { if (alive) setLive(email); })
            .catch(() => { if (alive) setLive("fehlt"); });
        return () => { alive = false; };
    }, [content, link.mailbox, link.messageId]);

    const kopie = React.useMemo<MailQuelle | undefined>(() => content?.source === "archive" ? {
        key: `link-${link.id}`,
        fetchParsed: async () => content.mail,
        fetchAttachmentBlob: (index) => linkedMailService.downloadAttachment(link.id, index),
        importAttachment: (index) => linkedMailService.importAttachment(link.id, index),
        fetchOriginal: () => linkedMailService.downloadOriginal(link.id),
    } : undefined, [content, link.id]);

    const laedt = !error && (!content || (content.source !== "unavailable" && live === null));
    return <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {error && <div role="alert" style={{ padding: 12 }}>{error}</div>}
        {content?.source === "unavailable" && (
            <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                <MailCaptureStatus archive={content.archive} />
                <div role="status">{content.message}</div>
            </div>
        )}
        <div style={{ flex: 1, minHeight: 0 }}>
            {laedt && <Spinner label="Mail wird geladen…" />}
            {live && live !== "fehlt" && <MailDetailWindowShell windowId={`linked-mail-${link.id}`} initial={{ email: live, mailbox: link.mailbox }} singleMessage onOpenMailFromSidebar={(mailbox, email) => {
                openMailDetailWindow(openWindow, { email, mailbox, singleMessage: true });
            }} />}
            {live === "fehlt" && kopie && <MailFileViewer quelle={kopie} wieImPostfach />}
        </div>
    </div>;
}
export function openLinkedMailWindow(openWindow: (config: OpenWindowConfig) => void, link: MailLinkDto): string {
    const id = `linked-mail-${link.id}`;
    openWindow({ id, title: link.subject || "Verknüpfte Mail", initialSize: { width: 960, height: 660 }, render: () => <LinkedMailWindow key={link.id} link={link} openWindow={openWindow} /> });
    return id;
}
